import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, errMsg} from "@/lib/config";
import { toast } from "sonner";
import { Loader2, Check, X, Clock, CreditCard, ArrowLeft, RefreshCw } from "lucide-react";
import { Reveal } from "@/lib/motion";
import PaymentPanel from "@/components/PaymentPanel";

const STATUS_LABEL = {
  pending_review: { label: "In attesa di revisione tecnica", color: "text-white/70", icon: Clock },
  approved: { label: "Approvato — pronto per il pagamento", color: "text-[#ff4b00]", icon: CreditCard },
  paid: { label: "Pagato — in preparazione", color: "text-[#22c55e]", icon: Check },
  in_production: { label: "In produzione", color: "text-[#22c55e]", icon: Loader2 },
  shipped: { label: "Spedito", color: "text-[#22c55e]", icon: Check },
  completed: { label: "Completato", color: "text-[#22c55e]", icon: Check },
  rejected: { label: "Non fattibile", color: "text-red-400", icon: X },
};

export default function QuotePage() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get(`/quotes/${id}`);
      setQuote(r.data);
    } catch (e) {
      toast.error("Preventivo non trovato");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const goCheckout = async () => {
    try {
      const r = await api.post("/payments/checkout", { quote_id: id, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e, "Errore avvio pagamento"));
    }
  };

  if (loading) return <div className="pt-40 text-center font-mono-tech text-white/60">Caricamento...</div>;
  if (!quote) return (
    <div className="pt-40 max-w-xl mx-auto px-6">
      <div className="font-display text-4xl">Preventivo non trovato</div>
      <Link to="/" className="btn-ghost mt-8 inline-flex"><ArrowLeft size={14} /> Home</Link>
    </div>
  );

  const STATUS_LABEL_MAP = {
    ...STATUS_LABEL,
    awaiting_bank_transfer: { label: "In attesa del bonifico bancario", color: "text-[#ff4b00]", icon: Clock },
  };
  const S = STATUS_LABEL_MAP[quote.status] || STATUS_LABEL.pending_review;

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <Reveal>
          <Link to="/" className="font-mono-tech text-white/60 hover:text-[#ff4b00]"><ArrowLeft size={12} className="inline mr-1" />Home</Link>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="num-chip my-6"><span className="dot" /> Preventivo · {String(id).slice(0, 8).toUpperCase()}</div>
          <div className="font-display text-4xl md:text-6xl leading-[0.95]">La tua richiesta.</div>
        </Reveal>

        <div className="mt-10 border border-[#1a1a1a] p-6 md:p-8 bg-[#0a0a0a] flex items-center justify-between">
          <div>
            <div className="font-mono-tech text-white/40 text-[10px] mb-2">Stato</div>
            <div className={`font-display text-2xl ${S.color}`}>{S.label}</div>
          </div>
          <button onClick={load} className="btn-ghost"><RefreshCw size={14} /> Aggiorna</button>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-[#1a1a1a] p-6">
            <div className="font-mono-tech text-white/40 text-[10px] mb-4">Specifiche</div>
            <div className="space-y-2 font-mono-tech text-[12px]">
              <div className="flex justify-between"><span className="text-white/40">Materiale</span><span>{quote.specs.material_name}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Spessore</span><span>{quote.specs.thickness_mm} mm</span></div>
              <div className="flex justify-between"><span className="text-white/40">Dimensioni</span><span>{quote.specs.width_mm}×{quote.specs.height_mm} mm</span></div>
              <div className="flex justify-between"><span className="text-white/40">Quantità</span><span>{quote.specs.quantity} pz</span></div>
              <div className="flex justify-between"><span className="text-white/40">Peso totale</span><span>{quote.estimate.total_weight_kg} kg</span></div>
            </div>
          </div>
          <div className="border border-[#1a1a1a] p-6">
            <div className="font-mono-tech text-white/40 text-[10px] mb-4">Importi</div>
            <div className="space-y-2 font-mono-tech text-[12px]">
              <div className="flex justify-between"><span className="text-white/40">Stima automatica (IVA incl.)</span><span>€ {quote.estimate.total_with_vat.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-white/40">Spedizione stimata</span><span>€ {quote.shipping.cost.toFixed(2)}</span></div>
              {quote.final_price != null && (
                <div className="flex justify-between mt-4 pt-4 border-t border-[#151515]">
                  <span className="font-display text-lg text-white">Prezzo definitivo</span>
                  <span className="font-display text-xl text-[#ff4b00]">€ {(quote.final_price + quote.shipping_cost).toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {quote.status === "approved" && (
          <PaymentPanel quote={quote} onPaid={load} />
        )}

        {quote.status === "awaiting_bank_transfer" && quote.payment?.bonifico && (
          <div className="mt-10 border border-[#ff4b00] bg-[#ff4b00]/5 p-6 md:p-8" data-testid="awaiting-bonifico">
            <div className="font-mono-tech text-[#ff4b00] mb-2">◑ In attesa del bonifico</div>
            <div className="font-display text-2xl md:text-3xl mb-1">Coordinate bonifico</div>
            <div className="font-mono-tech text-[11px] text-white/60 mb-6">Non appena riceviamo il pagamento passiamo in produzione.</div>
            <div className="space-y-2 font-mono-tech text-[12px]">
              <div className="flex justify-between border-b border-[#141414] py-2"><span className="text-white/50">Beneficiario</span><span>{quote.payment.bonifico.holder}</span></div>
              <div className="flex justify-between border-b border-[#141414] py-2"><span className="text-white/50">Banca</span><span>{quote.payment.bonifico.bank}</span></div>
              <div className="flex justify-between border-b border-[#141414] py-2"><span className="text-white/50">IBAN</span><span className="select-all">{quote.payment.bonifico.iban}</span></div>
              <div className="flex justify-between border-b border-[#141414] py-2"><span className="text-white/50">Causale</span><span className="select-all">{quote.payment.bonifico.causale}</span></div>
              <div className="flex justify-between py-2"><span className="font-display text-lg text-white">Totale</span><span className="font-display text-xl text-[#ff4b00]">€ {quote.payment.bonifico.amount.toFixed(2)}</span></div>
            </div>
          </div>
        )}

        {quote.status === "pending_review" && (
          <div className="mt-10 border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
            <div className="flex items-start gap-4">
              <Loader2 size={20} className="text-[#ff4b00] animate-spin" />
              <div>
                <div className="font-display text-lg">Revisione in corso</div>
                <div className="text-white/60 text-sm mt-2 max-w-lg">
                  Un tecnico Gefion sta controllando il tuo file DWG. Riceverai una email
                  entro 24h lavorative con il preventivo definitivo e il link di pagamento.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* Payment result pages */
export function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const quoteId = params.get("quote_id");
  const [status, setStatus] = useState("pending");
  const [tries, setTries] = useState(0);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      if (!sessionId) return;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (!alive) return;
        setStatus(r.data.payment_status);
        if (r.data.payment_status !== "paid" && tries < 8) {
          setTimeout(() => setTries((t) => t + 1), 2000);
        }
      } catch { /* ignore */ }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId, tries]);

  return (
    <div className="pt-40 pb-24 max-w-2xl mx-auto px-6 text-center">
      <div className="num-chip mx-auto mb-8"><span className="dot" /> Pagamento</div>
      {status === "paid" ? (
        <>
          <Check size={64} className="text-[#22c55e] mx-auto mb-8" />
          <div className="font-display text-5xl md:text-6xl mb-4">Pagamento ricevuto.</div>
          <div className="text-white/60 max-w-md mx-auto">Grazie! Il tuo ordine passa ora in produzione. Ti aggiorneremo via email.</div>
        </>
      ) : (
        <>
          <Loader2 size={64} className="text-[#ff4b00] mx-auto mb-8 animate-spin" />
          <div className="font-display text-4xl mb-4">Verifica pagamento in corso...</div>
          <div className="text-white/60">Attendi qualche secondo, sto controllando Stripe.</div>
        </>
      )}
      <div className="mt-10 flex justify-center gap-4">
        <button onClick={() => nav(`/preventivo/${quoteId}`)} className="btn-ghost">Vedi preventivo</button>
        <button onClick={() => nav("/")} className="btn-primary">Home</button>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const quoteId = params.get("quote_id");
  return (
    <div className="pt-40 pb-24 max-w-2xl mx-auto px-6 text-center">
      <X size={64} className="text-white/60 mx-auto mb-8" />
      <div className="font-display text-5xl md:text-6xl mb-4">Pagamento annullato.</div>
      <div className="text-white/60 max-w-md mx-auto">Nessun addebito effettuato. Puoi tornare al preventivo e riprovare quando vuoi.</div>
      <div className="mt-10">
        <Link to={quoteId ? `/preventivo/${quoteId}` : "/"} className="btn-primary">Torna al preventivo</Link>
      </div>
    </div>
  );
}
